import { cva } from "class-variance-authority";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import Avatar from "@/components/ui/avatar/avatar";
import { generateKey } from "@/libs/generate-key";

type AvatarGroupProps = Omit<
  React.ComponentProps<typeof Avatar>,
  "src" | "name"
> & {
  data: {
    src: string;
    name: string;
  }[];
  limit?: number;
  showName?: boolean;
  showMore?: boolean;
  labelClassName?: string;
};

const avatarGroupVariants = cva(
  cn(
    /* sorted */
    "flex flex-row-reverse justify-end",
    "space-x-reverse",
    "*:ring *:ring-background",
  ),
  {
    variants: {
      size: {
        xs: "-space-x-1",
        sm: "-space-x-2",
        base: "-space-x-2",
        md: "-space-x-2",
        lg: "-space-x-3",
        xl: "-space-x-3",
        "2xl": "-space-x-3",
        "3xl": "-space-x-3",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

const avatarGroupNameVariants = cva("text-text-dark-high", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-xs",
      base: "text-xs",
      md: "text-sm",
      lg: "text-sm",
      xl: "text-sm",
      "2xl": "text-sm",
      "3xl": "text-sm",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

function renderNames(names: string[], limit = names.length) {
  if (names.length === 0) return "";

  const visibleNames = names.slice(0, limit);
  const [first, second, ...rest] = visibleNames;

  if (!second) return first;

  const restText = rest.length > 0 ? `, ${rest.join(", ")}` : "";

  return `${first} & ${second}${restText}`;
}

function AvatarGroup({
  data = [],
  limit = 3,
  size = "base",
  className,
  showName = false,
  showMore = true,
  labelClassName,
  ...props
}: AvatarGroupProps) {
  if (isEmpty(data)) return null;

  const names = data.map((item) => item.name || "");
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <div className={cn(avatarGroupVariants({ size }), "")}>
        {data
          .slice(0, limit)
          // The container is flex-row-reverse (so the first avatar paints on
          // top of the rest); reversing here puts data[0] leftmost, matching
          // the name label.
          .toReversed()
          .map((item, index) => (
            <Avatar
              key={generateKey(item.name + index)}
              src={item.src}
              name={item.name}
              size={size}
              {...props}
            />
          ))}
      </div>
      {showName && (
        <p className={cn(avatarGroupNameVariants({ size }), labelClassName)}>
          {renderNames(names, limit)}
        </p>
      )}
      {showMore && data.length > limit && (
        <p
          className={cn(
            /* sorted */
            avatarGroupNameVariants({ size }),
            "shrink-0",
            labelClassName,
          )}
        >
          +{data.length - limit}
        </p>
      )}
    </div>
  );
}

export { AvatarGroup };
